import { useState } from 'react';
import type { FormEvent } from 'react';
import { UserDialog } from './UserDialog';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Field } from '../../components/ui/Field';
import { useToast } from '../../components/ui/Toast';
import {
  useAuditLog,
  useBackup,
  useChangePassword,
  useUsers,
  userMutations,
} from '../../lib/queries';
import { ROLE_LABELS } from '../../lib/labels';
import { formatDateTime } from '../../lib/dates';
import { ApiError } from '../../lib/api';
import type { SessionUser } from '../../lib/types';
import './einstellungen.css';

export function EinstellungenView({ user }: { user: SessionUser }) {
  const isAdmin = user.role === 'admin';

  return (
    <div className="view">
      <header className="view-head">
        <div className="view-title">
          <span className="label">System</span>
          <h1>Einstellungen</h1>
          <p className="view-lead">Konto, Zugänge, Datensicherung</p>
        </div>
      </header>

      <PasswordSection />
      {isAdmin ? <UserSection currentUser={user} /> : null}
      {isAdmin ? <DataSection /> : null}
      {isAdmin ? <AuditSection /> : null}
    </div>
  );
}

function PasswordSection() {
  const toast = useToast();
  const changePassword = useChangePassword();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (newPassword !== repeat) {
      setError('Die beiden neuen Passwörter stimmen nicht überein.');
      return;
    }

    changePassword.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          toast.success('Passwort geändert.');
          setCurrentPassword('');
          setNewPassword('');
          setRepeat('');
        },
        onError: (apiError) =>
          setError(apiError instanceof ApiError ? apiError.message : 'Ändern fehlgeschlagen.'),
      },
    );
  };

  const valid = currentPassword && newPassword.length >= 8 && repeat;

  return (
    <section className="card settings-card">
      <div className="settings-head">
        <h2>Eigenes Passwort</h2>
        <p className="hint">Mindestens 8 Zeichen.</p>
      </div>

      <form className="settings-form" onSubmit={submit} noValidate>
        <Field label="Aktuelles Passwort">
          {(props) => (
            <input
              {...props}
              className="input"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          )}
        </Field>
        <Field label="Neues Passwort">
          {(props) => (
            <input
              {...props}
              className="input"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          )}
        </Field>
        <Field label="Neues Passwort wiederholen">
          {(props) => (
            <input
              {...props}
              className="input"
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(event) => setRepeat(event.target.value)}
            />
          )}
        </Field>

        {error ? (
          <p className="form-error span-2" role="alert">
            {error}
          </p>
        ) : null}

        <div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!valid || changePassword.isPending}
          >
            {changePassword.isPending ? 'Wird geändert …' : 'Passwort ändern'}
          </button>
        </div>
      </form>
    </section>
  );
}

function UserSection({ currentUser }: { currentUser: SessionUser }) {
  const toast = useToast();
  const users = useUsers(true);
  const remove = userMutations.useDelete();
  const [editing, setEditing] = useState<SessionUser | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<SessionUser | null>(null);

  return (
    <section className="card settings-card">
      <div className="settings-head">
        <div>
          <h2>Zugänge</h2>
          <p className="hint">
            Wer im Büro mit der Schichtplanung arbeitet. Betrachter dürfen ausschließlich lesen.
          </p>
        </div>
        <button type="button" className="btn btn-signal" onClick={() => setEditing(null)}>
          Zugang anlegen
        </button>
      </div>

      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Benutzer</th>
              <th>Name</th>
              <th>Rolle</th>
              <th>Status</th>
              <th>Zuletzt angemeldet</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(users.data ?? []).map((entry) => (
              <tr key={entry.id}>
                <td className="numeric">{entry.username}</td>
                <td>
                  <strong>{entry.displayName}</strong>
                  {entry.id === currentUser.id ? <span className="badge">Das bist du</span> : null}
                </td>
                <td>{ROLE_LABELS[entry.role]}</td>
                <td>
                  <span className={`badge ${entry.active ? 'status-genehmigt' : ''}`}>
                    {entry.active ? 'Aktiv' : 'Gesperrt'}
                  </span>
                </td>
                <td className="numeric faint">
                  {entry.lastLoginAt ? formatDateTime(entry.lastLoginAt) : 'nie'}
                </td>
                <td className="align-right">
                  <div className="row-actions">
                    <button type="button" className="btn btn-small" onClick={() => setEditing(entry)}>
                      Bearbeiten
                    </button>
                    {entry.id !== currentUser.id ? (
                      <button
                        type="button"
                        className="btn btn-small btn-danger"
                        onClick={() => setDeleting(entry)}
                      >
                        Löschen
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing !== undefined ? (
        <UserDialog user={editing} onClose={() => setEditing(undefined)} />
      ) : null}

      {deleting ? (
        <ConfirmDialog
          title="Zugang löschen"
          message={`Zugang "${deleting.username}" wirklich löschen?`}
          confirmLabel="Löschen"
          busy={remove.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() =>
            remove.mutate(
              { id: deleting.id },
              {
                onSuccess: () => {
                  toast.success('Zugang gelöscht.');
                  setDeleting(null);
                },
                onError: (error) => {
                  toast.error(error instanceof ApiError ? error.message : 'Löschen fehlgeschlagen.');
                  setDeleting(null);
                },
              },
            )
          }
        />
      ) : null}
    </section>
  );
}

function DataSection() {
  const toast = useToast();
  const backup = useBackup();

  return (
    <section className="card settings-card">
      <div className="settings-head">
        <div>
          <h2>Datensicherung</h2>
          <p className="hint">
            Der Server sichert die Datenbank automatisch beim Start und danach alle sechs Stunden. Die
            letzten 30 Sicherungen bleiben erhalten.
          </p>
        </div>
      </div>

      <div className="row row-wrap">
        <button
          type="button"
          className="btn"
          disabled={backup.isPending}
          onClick={() =>
            backup.mutate(undefined, {
              onSuccess: (result) => toast.success('Sicherung erstellt.', result.file),
              onError: () => toast.error('Sicherung fehlgeschlagen.'),
            })
          }
        >
          {backup.isPending ? 'Wird gesichert …' : 'Jetzt sichern'}
        </button>

        <a className="btn" href="/api/export" download>
          Datenauszug herunterladen (JSON)
        </a>
      </div>
    </section>
  );
}

function AuditSection() {
  const audit = useAuditLog(true);
  const [expanded, setExpanded] = useState(false);
  const entries = audit.data ?? [];
  const visible = expanded ? entries : entries.slice(0, 12);

  return (
    <section className="card settings-card">
      <div className="settings-head">
        <div>
          <h2>Änderungsprotokoll</h2>
          <p className="hint">Wer wann was geändert hat.</p>
        </div>
      </div>

      {entries.length === 0 ? (
        <p className="hint">Noch keine Einträge.</p>
      ) : (
        <>
          <ol className="audit-list">
            {visible.map((entry) => (
              <li key={entry.id}>
                <span className="audit-time numeric">{formatDateTime(entry.createdAt)}</span>
                <span className="audit-actor">{entry.actor}</span>
                <span className="audit-summary">{entry.summary || `${entry.entity} ${entry.action}`}</span>
              </li>
            ))}
          </ol>
          {entries.length > 12 ? (
            <button type="button" className="btn btn-ghost btn-small" onClick={() => setExpanded(!expanded)}>
              {expanded ? 'Weniger anzeigen' : `Alle ${entries.length} Einträge anzeigen`}
            </button>
          ) : null}
        </>
      )}
    </section>
  );
}
